import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import type React from "react";
import { IconFileExport } from "@tabler/icons-react";
import { Modal } from "@/components/Modal/Modal";
import { MODAL_Z_INDEX_MAP } from "@/components/ModalWrapper/ModalWrapper";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { InputBlob } from "@/components/Inputs/InputBlob";
import { ModalErrorSummary } from "@/components/ModalErrorSummary/ModalErrorSummary";
import { OTP_LENGTH, useOtpDigits } from "@/components/PAddSenderAddress/useOtpDigits";
import { useToast } from "@/components/Toast/Toast";
import { useFocusTrap } from "@/lib/a11y/useFocusTrap";
import { translateBackendError } from "@/lib/i18n/translateBackendError";
import {
  downloadGuestDataExport,
  requestGuestDataExportCode,
} from "@/lib/api/clients/DataExportClient";
import type { Guest } from "@/types/Guest";
import { guestDataExportFileName } from "./dataExportFileName";
import { useGuestDataExportAllowed } from "./useGuestDataExportAllowed";

type Props = {
  guest: Pick<Guest, "guestId" | "firstName" | "lastName">;
  eventId: string | undefined;
};

/**
 * The guest popup's "Datenauskunft" (Art. 15 / 20 DSGVO): everything stored
 * about this guest as one JSON file — the consent rows with wording, time and
 * source among it, which the popup itself no longer shows (Nikita
 * 29.10.2026). Rendered only for a login that may export (see
 * `useGuestDataExportAllowed`); the server re-checks every step.
 */
export function GuestDataExportButton({ guest, eventId }: Props) {
  const t = useTranslations("guestDataExport");
  const allowed = useGuestDataExportAllowed(eventId);
  const [open, setOpen] = useState(false);
  if (!allowed || !eventId || !guest.guestId) return null;
  return (
    <>
      <button
        type="button"
        data-testid="guest-data-export"
        title={t("button")}
        aria-label={t("button")}
        onClick={() => setOpen(true)}
        className="cursor-pointer p-1 font-tertiary hover:font-primary transition-colors"
      >
        <IconFileExport size={18} aria-hidden />
      </button>
      {open && (
        <GuestDataExportDialog
          guest={{ guestId: guest.guestId, firstName: guest.firstName, lastName: guest.lastName }}
          eventId={eventId}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

type DialogProps = {
  guest: { guestId: string; firstName?: string; lastName?: string };
  eventId: string;
  onClose: () => void;
};

const OTP_ID_PREFIX = "guest-data-export-otp";

/**
 * Above every host the guest popup opens in — the send dialog's opt-in
 * warning raises it to z-[10070] (`OptInWarningBlock`); the Modal default
 * (z-900) would open BEHIND it there.
 */
const DIALOG_Z = MODAL_Z_INDEX_MAP.overGuestPopup;

export function GuestDataExportDialog({ guest, eventId, onClose }: DialogProps) {
  const t = useTranslations("guestDataExport");
  const tErr = useTranslations("errors");
  const { showToast } = useToast();
  const otp = useOtpDigits(OTP_ID_PREFIX);
  const [step, setStep] = useState<"intro" | "code">("intro");
  const [busy, setBusy] = useState<null | "request" | "download">(null);
  const [error, setError] = useState("");
  // One request at a time — a double click must not mail two codes or burn
  // the code on a second GET.
  const inFlight = useRef(false);
  // Set on EVERY mount: <StrictMode> (main.tsx) runs mount → unmount → mount
  // in a dev build, and a ref only ever cleared would stay false for good.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // Trap on the Modal root (header Close reachable), first focus on the panel;
  // focus returns to the header icon on close.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(panelRef, true, dialogRef);

  const sendCode = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy("request");
    setError("");
    try {
      await requestGuestDataExportCode(eventId, guest.guestId);
      if (!alive.current) return;
      setStep("code");
      otp.clear();
    } catch (err) {
      if (alive.current) setError(translateBackendError(tErr, err as Error));
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(null);
    }
  }, [eventId, guest.guestId, otp, tErr]);

  const download = useCallback(async () => {
    if (inFlight.current || !otp.isComplete) return;
    inFlight.current = true;
    setBusy("download");
    setError("");
    try {
      await downloadGuestDataExport(
        eventId,
        guest.guestId,
        otp.code,
        guestDataExportFileName(t("fileNamePrefix"), guest),
      );
      if (!alive.current) return;
      showToast(t("downloaded"), "success");
      onClose();
    } catch (err) {
      if (!alive.current) return;
      const e = err as Error;
      setError(translateBackendError(tErr, e));
      // A wrong or expired code: start the boxes over.
      if (e?.message === "invalidOtp") otp.clear();
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(null);
    }
  }, [eventId, guest, otp, onClose, showToast, t, tErr]);

  // Nothing in flight may be cut off by a stray backdrop click or Escape.
  const close = useCallback(() => {
    if (!inFlight.current) onClose();
  }, [onClose]);

  // Typing again retires the last error; Enter on a full code downloads.
  const onDigit = (value: string, index: number) => {
    setError("");
    otp.handleChange(value, index);
  };
  const onPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    setError("");
    otp.handlePaste(event);
  };
  const onDigitKey = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void download();
      return;
    }
    otp.handleKeyDown(event, index);
  };

  return (
    <Modal
      open
      onClose={close}
      title={t("title")}
      size="md"
      hideHeaderBorder
      zClassName={DIALOG_Z}
      // Once a code is on its way, a stray press beside the panel must not
      // throw the boxes away; Escape and the X still close.
      closeOnBackdrop={step === "intro"}
      panelRef={panelRef}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("title")}
        tabIndex={-1}
        className="flex flex-col outline-none"
        data-testid="guest-data-export-dialog"
      >
        {step === "intro" ? (
          <p className="body-2-regular" data-testid="guest-data-export-intro">
            {t("intro")}
          </p>
        ) : (
          <>
            <p className="body-2-regular" data-testid="guest-data-export-code-sent">
              {t("codeSent")}
            </p>
            <div className="mt-6 flex flex-row space-x-1" role="group" aria-label={t("codeLabel")}>
              {otp.digits.map((digit, index) => (
                <InputBlob
                  key={index}
                  index={index}
                  idPrefix={OTP_ID_PREFIX}
                  testId={`${OTP_ID_PREFIX}-${index}`}
                  value={digit}
                  isActive={index === otp.activeIndex}
                  changAction={onDigit}
                  keyDownAction={onDigitKey}
                  onPaste={onPaste}
                  inputMode="numeric"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={index === 0 ? OTP_LENGTH : 1}
                  ariaLabel={t("codeDigitLabel", { index: index + 1, count: OTP_LENGTH })}
                  disabled={busy !== null}
                />
              ))}
            </div>
          </>
        )}
        <ModalErrorSummary errors={[error]} />
        <div className="mt-6 flex flex-row flex-wrap justify-end gap-2">
          {step === "intro" ? (
            <>
              <SecondaryButton text={t("cancel")} width="w-auto" onClick={close} disabled={busy !== null} />
              <PrimaryButton
                text={t("sendCode")}
                width="w-auto"
                testId="guest-data-export-send"
                onClick={() => void sendCode()}
                loading={busy === "request"}
                disabled={busy !== null}
              />
            </>
          ) : (
            <>
              <SecondaryButton
                text={t("resend")}
                width="w-auto"
                testId="guest-data-export-resend"
                onClick={() => void sendCode()}
                loading={busy === "request"}
                disabled={busy !== null}
              />
              <PrimaryButton
                text={t("download")}
                width="w-auto"
                testId="guest-data-export-download"
                onClick={() => void download()}
                loading={busy === "download"}
                disabled={busy !== null || !otp.isComplete}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
